<template>
  <footer class="app-footer">
    <div class="footer-main">
      <div class="footer-inner">
        <div class="footer-brand mr-70px">
          <img :src="logo" alt="ReMaps" class="footer-logo" />
          <p class="footer-tagline whitespace-nowrap">重新定向职业地图 助力海外就业落地</p>
        </div>
        <div class="footer-col">
          <h4 class="footer-col-title">关于我们</h4>
          <a
            v-for="link in aboutLinks"
            :key="link.label"
            :href="link.path"
            class="footer-link"
          >
            {{ link.label }}
          </a>
          <a class="footer-link" @click.prevent="handleHiringSolutionsClick">招聘解决方案</a>
          <a class="footer-link" @click.prevent="handleOurTeamClick">我们的团队</a>
          <a class="footer-link" @click.prevent="handleIndustrySolutionsClick">行业解决方案</a>
        </div>
        <div class="footer-col">
          <h4 class="footer-col-title">我们覆盖的领域</h4>
          <a v-for="(sector, index) in sectors" :key="sector" class="footer-link" @click.prevent="handleSectorClick(index)">{{ sector }}</a>
        </div>
        <div class="footer-col">
          <h4 class="footer-col-title">联系我们与办公地点</h4>
          <a :href="`mailto:${contactEmail}`" class="footer-link footer-email">{{ contactEmail }}</a>
          <span v-for="loc in locations" :key="loc" class="footer-text">{{ loc }}</span>
        </div>
        <div class="footer-qr">
          <img :src="qr1" alt="QR Code" class="qr-img" />
          <img :src="qr2" alt="QR Code" class="qr-img" />
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="footer-bottom-inner">
        <div class="footer-bottom-left">
          <span class="footer-copyright mr-70px">
            &copy; 2026 睿际人才科技服务(成都)有限公司，保留所有权利。
          </span>
          <router-link to="/legal-statement" class="footer-legal-link">法律声明</router-link>
          <span class="footer-separator">|</span>
          <router-link to="/privacy-policy" class="footer-legal-link">隐私政策</router-link>
          <span class="footer-separator">|</span>
          <a class="footer-legal-link" @click.prevent="handleCookiePreferences">Cookie 偏好设置</a>
        </div>
        <div class="footer-bottom-col" aria-hidden="true">
          <span class="footer-bottom-col-text">我们的服务</span>
        </div>
        <div class="footer-bottom-col" aria-hidden="true">
          <span class="footer-bottom-col-text">工业自动化与制造</span>
        </div>
        <div class="footer-bottom-qr" aria-hidden="true"></div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { useCookieConsent } from '@/composables/useCookieConsent'
import logo from '@/assets/images/home-logo.png'
import qr1 from '@/assets/images/qr-code-1.png'
import qr2 from '@/assets/images/qr-code-2.png'

const route = useRoute()
const router = useRouter()
const { showCookieSettings } = useCookieConsent()

const contactEmail = 'bd@remaps.group'

/** Maps footer sector index → homepage sector group id */
const sectorGroupIds = ['it-digital', 'eng-hw', 'ind-auto', 'auto-trades', 'logistics']

const aboutLinks = [
  { label: '我们的服务', path: '/our-services' },
  { label: '寻找岗位', path: '/find-talent' },
  { label: '行业动态', path: '/industry-trends' },
]

const sectors = ['IT / 数字化', '工程与硬件技术', '工业自动化与制造', '汽车与技工岗位', '物流与供应链管理']

const locations = ['中国·香港', '中国·成都', '德国·法兰克福']

function scrollToElement(id: string) {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

function handleOurTeamClick() {
  if (route.path === '/about-us') {
    scrollToElement('our-team')
  } else {
    router.push('/#our-team')
  }
}

function handleHiringSolutionsClick() {
  if (route.path === '/our-services') {
    scrollToElement('headhunting')
  } else {
    router.push('/our-services#headhunting')
  }
}

function handleIndustrySolutionsClick() {
  if (route.path === '/our-services') {
    scrollToElement('industry-solutions')
  } else {
    router.push('/#industry-solutions')
  }
}

function handleSectorClick(index: number) {
  const groupId = sectorGroupIds[index]
  if (!groupId) return
  if (route.path === '/') {
    scrollToElement(groupId)
  } else {
    router.push(`/#${groupId}`)
  }
}

function handleCookiePreferences() {
  showCookieSettings()
}
</script>

<style lang="scss" scoped>
.app-footer {
  width: 100%;
  background-color: #001234;
}

.footer-main {
  border-top: 1px dashed #252E51;
  display: flex;
  align-items: center;
  padding-top: 60px;
  padding-bottom: 60px;
}

.footer-inner {
  display: flex;
  align-items: flex-start;
  gap: clamp(24px, 4.167vw, 80px);
  width: 100%;
  max-width: var(--content-width);
  margin: 0 auto;
  padding: 0 var(--content-px);
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-logo {
  width: 82px;
  height: 22px;
  display: block;
}

.footer-tagline {
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
  margin: 0;
}

.footer-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-col-title {
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  white-space: nowrap;
}

.footer-link {
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: #fff;
  }

  &--pending {
    cursor: default;

    &:hover {
      color: rgba(255, 255, 255, 0.55);
    }
  }
}

.footer-text {
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
  white-space: nowrap;
}

.footer-qr {
  display: flex;
  gap: clamp(12px, 1.563vw, 30px);
  flex-shrink: 0;

  .qr-img {
    width: 100px;
    height: 100px;
    display: block;
  }
}

/* ---- Bottom bar ---- */
.footer-bottom {
  border-top: 1px dashed #252E51;
  overflow: hidden;
}

.footer-bottom-inner {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: clamp(24px, 4.167vw, 80px);
  width: 100%;
  height: 74px;
  max-width: var(--content-width);
  margin: 0 auto;
  padding: 24px var(--content-px) 0;
}

.footer-bottom-col {
  display: none;
}

.footer-bottom-col-text {
  font-size: 14px;
  white-space: nowrap;
}

.footer-bottom-qr {
  display: none;
}

.footer-bottom-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.footer-copyright {
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
}

.footer-legal-link {
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
}

.footer-separator {
  color: rgba(255, 255, 255, 0.25);
  font-size: 14px;
  user-select: none;
}

@media (max-width: 767px) {
  .footer-main {
    padding: 48px 24px;
    align-items: flex-start;
  }

  .footer-inner {
    flex-direction: column;
    gap: 40px;
  }

  .footer-bottom-inner {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    height: auto;
    padding: 12px 24px;
  }

  .footer-bottom-left {
    gap: 8px;
  }
}
</style>
